// components/assigned-tasks/assigned-tasks.component.jsx
import React, { useState, useEffect, useRef, useMemo } from "react";
import { useSessionData } from "../session-data/SessionDataProvider";
import { useSelector } from "react-redux";
import "./assigned-tasks.styles.scss";
import QuickNotesSection from "./QuickNotesSection";
import QuickTasksSection from "./QuickTasksSection";
import AssignedTasksSection from "./AssignedTasksSection";
import { applyListOptions, sortByName } from "../../utils/assignedListSorting";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import timeGridPlugin from "@fullcalendar/timegrid";
import rrulePlugin from "@fullcalendar/rrule";
import momentTimezonePlugin from "@fullcalendar/moment-timezone";
import interactionPlugin, { Draggable } from "@fullcalendar/interaction";
import "./mini-calendar.styles.scss";
import {
  IoCheckmarkDone,
  IoChevronForwardOutline,
  IoArrowUp,
  IoArrowDown,
  IoEyeOutline,
  IoEyeOffOutline,
  IoExpandOutline,
  IoContractOutline,
} from "react-icons/io5";
const AssingedTasks = ({ setNumOfPrTasks, display }) => {
  const { inboxTasks, errors } = useSessionData();
  const stationData = useSelector((state) => state.space.stationData);
  const assignedTasks = useSelector((state) => state.tasks.tasks);
  const notes = useSelector((state) => state.notes.notes);
  const assignedColTasks = assignedTasks;
  const assignedColSpaces = useMemo(() => assignedTasks.map((task) => task.fromSpaceId), [assignedTasks]);
  const [expandAll, setExpandAll] = useState(null);
  const [listOptions, setListOptions] = useState({
    key: "createdAt",
    dir: "desc",
    hideDone: false,
  });
  const [modalIsOpen, setModalIsOpen] = useState(false);
  const spaceData = useSelector((state) => state.space.spaceData);
  const [showtaskColumn, setShowTaskColumn] = useState(false);
  const [slotDuration, setSlotDuration] = useState("00:15:00");
  const miniCalendarRef = useRef();
  const visibleNotes = useMemo(
    () => applyListOptions(notes, listOptions, "note"),
    [notes, listOptions],
  );
  const visibleInboxTasks = useMemo(
    () => applyListOptions(inboxTasks, listOptions, "task"),
    [inboxTasks, listOptions],
  );
  const visibleAssignedTasks = useMemo(
    () => applyListOptions(assignedColTasks, listOptions, "task"),
    [assignedColTasks, listOptions],
  );
  const sortedAssignedSpaces = useMemo(() => {
    const assignedSpaceIds = new Set(assignedColSpaces);
    return sortByName(
      (spaceData || []).filter((space) => assignedSpaceIds.has(space.spaceId)),
    );
  }, [spaceData, assignedColSpaces]);
  useEffect(() => {
    setNumOfPrTasks(assignedTasks.filter((task) => task.done === false).length);
  }, [assignedTasks, setNumOfPrTasks]);
  const [trassignedTasks, setTrAssignedTasks] = useState([]);
  useEffect(() => {
    const assignedTasksTransformed = assignedColTasks.map((astask) => {
      return {
        id: astask.id,
        title: astask.title,
        description: astask.description,
        done: astask.done,
        start: astask.startdate != null && astask.startdate.toDate(),
        end: astask.enddate != null && astask.enddate.toDate(),
        allDay: astask.allDay,
        durationn: astask.duration,
        createdBy: astask.createdBy,
        createdAt: astask.createdAt,
        assign: astask.assign,
        phase: astask.phase,
        priority: astask.priority,
        progress: astask.progress,
        type: astask.type,
        rrule: astask.rrule,
        // color: "#909296",
        backgroundColor:
          astask.type === "habitReminder"
            ? "#1A82C4"
            : spaceData
                ?.filter((space) => space.spaceId === astask.fromSpaceId)
                .map((data) => data.color),
        borderColor: spaceData
          ?.filter((space) => space.spaceId === astask.fromSpaceId)
          .map((data) => data.color),
        // className: "this_project-items",
        fromSpaceId: astask.fromSpaceId,
        fromStationId: astask.fromStationId,
      };
    });
    setTrAssignedTasks([...assignedTasksTransformed]);
  }, [assignedColTasks, spaceData]);
  useEffect(() => {
    let containerEl = document.getElementById("external-assignedTasks");
    // // initialize the external events
    const draggable = new Draggable(containerEl, {
      itemSelector: ".fc-event",
      eventData: function (eventEl) {
        return {
          id: eventEl.getAttribute("id"),
          title: eventEl.getAttribute("title"),
          start: eventEl.getAttribute("start"),
          end: eventEl.getAttribute("end"),
          duration: eventEl.getAttribute("duration"),
          fromSpaceId: eventEl.getAttribute("fromSpaceId"),
          fromStationId: eventEl.getAttribute("fromStationId"),
        };
      },
    });
    return () => draggable.destroy();
  }, []);
  const MIN_SLOT_MINUTES = 15; // minimum zoom level (15 minutes)
  const MAX_SLOT_MINUTES = 60; // maximum zoom level (4 hours)
  const BASE_LABEL_MINUTES = 60; // ensure at least hourly labels
  const formatDuration = (totalMinutes) => {
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    return `${hours.toString().padStart(2, "0")}:${minutes
      .toString()
      .padStart(2, "0")}:00`;
  };
  const updateZoom = (newMinutes) => {
    // clamp zoom
    if (newMinutes < MIN_SLOT_MINUTES) newMinutes = MIN_SLOT_MINUTES;
    if (newMinutes > MAX_SLOT_MINUTES) newMinutes = MAX_SLOT_MINUTES;
    const newDuration = formatDuration(newMinutes);
    // label interval at least hourly or twice the slot, whichever is larger
    const labelMinutes = Math.max(BASE_LABEL_MINUTES, newMinutes * 2);
    const newLabel = formatDuration(labelMinutes);
    setSlotDuration(newDuration);
    const api = miniCalendarRef.current.getApi();
    api.setOption("slotDuration", newDuration);
    api.setOption("slotLabelInterval", newLabel);
    api.updateSize();
  };
  const zoomIn = () => {
    const [h, m] = slotDuration.split(":").map(Number);
    const totalMinutes = h * 60 + m;
    updateZoom(Math.floor(totalMinutes / 2));
  };
  const zoomOut = () => {
    const [h, m] = slotDuration.split(":").map(Number);
    const totalMinutes = h * 60 + m;
    updateZoom(totalMinutes * 2);
  };
  return (
    <div className="assigned__cal" style={{ display: display }}>
      <div
        className={
          !showtaskColumn
            ? "assigned__taskscolumn"
            : "assigned__taskscolumn-half"
        }
      >
        <div
          className={showtaskColumn ? "assignedTasksHide" : "assignedTasks"}
          id="external-assignedTasks"
        >
          {Object.values(errors).map((message) => <p key={message} role="status">{message}</p>)}
          <div className="at__fromspace-list">
            <QuickNotesSection
              color={"#1A82C4"}
              name={"Quick Notes"}
              notes={visibleNotes}
              expandAll={expandAll}
            />
          </div>
          <div className="at__fromspace-list">
            <QuickTasksSection
              color={"#1A82C4"}
              name={"Quick Tasks"}
              inboxTasks={visibleInboxTasks}
              expandAll={expandAll}
            />
          </div>
          {assignedColTasks.length === 0 ? (
            <div className="at__noTasks">
              <IoCheckmarkDone size="4em" />
              <p>All tasks are done!</p>
            </div>
          ) : (
            <>
              {sortedAssignedSpaces.map((data) => {
                const { spaceId, color, name } = data;
                return (
                  <div key={spaceId}>
                    <AssignedTasksSection
                      spaceId={spaceId}
                      stationData={sortByName(
                        stationData?.filter(
                          (station) => station.fromSpaceId === spaceId,
                        ) || [],
                      )}
                      color={color}
                      name={name}
                      assignedColTasks={visibleAssignedTasks}
                      expandAll={expandAll}
                    />
                  </div>
                );
              })}
            </>
          )}
        </div>
        <div className="assignedTasks__options">
          <div className="assignedTasks__sort">
            <select
              aria-label="Sort tasks and notes"
              value={listOptions.key}
              onChange={(e) =>
                setListOptions((prev) => ({
                  ...prev,
                  key: e.target.value,
                }))
              }
            >
              <option value="createdAt">Created</option>
              <option value="startdate">Start date</option>
              <option value="enddate">End date</option>
              <option value="title">Alphabetical</option>
              <option value="priority">Priority</option>
            </select>
            <button
              type="button"
              className="assignedTasks__direction"
              title={listOptions.dir === "asc" ? "Ascending" : "Descending"}
              onClick={() =>
                setListOptions((prev) => ({
                  ...prev,
                  dir: prev.dir === "asc" ? "desc" : "asc",
                }))
              }
            >
              {listOptions.dir === "asc" ? <IoArrowUp /> : <IoArrowDown />}
            </button>
          </div>
          <div className="assignedTasks__optionsRight">
            <button
              type="button"
              className="assignedTasks__expandAll"
              title={expandAll === true ? "Collapse all" : "Expand all"}
              onClick={() =>
                setExpandAll((prev) => (prev === true ? false : true))
              }
            >
              {expandAll === true ? <IoContractOutline /> : <IoExpandOutline />}
            </button>
            <label
              className={`assignedTasks__hideDone ${
                listOptions.hideDone ? "active" : ""
              }`}
              title="Hide completed"
            >
              <input
                type="checkbox"
                checked={listOptions.hideDone}
                onChange={() =>
                  setListOptions((prev) => ({
                    ...prev,
                    hideDone: !prev.hideDone,
                  }))
                }
              />
              {listOptions.hideDone ? <IoEyeOffOutline /> : <IoEyeOutline />}
              <span>Done</span>
            </label>
          </div>
        </div>
      </div>
      <div
        className="assigned__taskscolumn-hide"
        onClick={() => setShowTaskColumn(!showtaskColumn)}
        style={{ display: !modalIsOpen && "hidden" }}
      >
        <div className="assigned__taskscolumn-hidetext">MyTasks</div>
        <div
          className={
            !showtaskColumn
              ? "assigned__taskscolumn-hidetasks"
              : "assigned__taskscolumn-hidetasks-rotated"
          }
        >
          <IoChevronForwardOutline size="1em" />
        </div>
      </div>
    </div>
  );
};
export default AssingedTasks;
